import { getContainerTypes } from "../../productCommons/index.js";
import { toNumberOrNull } from "../../helpers/index.js";
import {
  validatePriceTierDerivation,
  validateProductPriceTierDerivation,
} from "../validateDerivation/index.js";

/**
 * Calculates the derived price of a product.
 * Unknowns allow for some of our pricing which is of type Decimal
 * Automatically handles conversion of potentially null/undefined/Decimal values to numbers.
 */
export const getDerivedPrice = (parameters: {
  basePrice: number;
  containerType: string | null;
  productPriceTier: {
    price: unknown;
    derivationType: "custom" | "flat" | "inherit" | "none" | "percentage";
    derivationValue: unknown;
  };
  priceTier: {
    derivationType: "base" | "custom" | "flat" | "percentage";
    derivationValue: unknown;
  };
  priceTierContainerDiscounts: Array<{
    containerType: "keg" | "can" | "cask" | "bottle" | "bagInBox";
    derivationType: "flat" | "percentage";
    derivationValue: unknown;
  }>;
}): number => {
  const {
    basePrice,
    containerType,
    productPriceTier: rawProductPriceTier,
    priceTier: rawPriceTier,
    priceTierContainerDiscounts: rawContainerDiscounts,
  } = parameters;

  const productPriceTier = {
    price: toNumberOrNull(rawProductPriceTier.price),
    derivationType: rawProductPriceTier.derivationType,
    derivationValue: toNumberOrNull(rawProductPriceTier.derivationValue),
  };

  const priceTier = {
    derivationType: rawPriceTier.derivationType,
    derivationValue: toNumberOrNull(rawPriceTier.derivationValue),
  };

  const priceTierContainerDiscounts = rawContainerDiscounts.map((discount) => ({
    containerType: discount.containerType,
    derivationType: discount.derivationType,
    derivationValue: toNumberOrNull(discount.derivationValue),
  }));

  if (
    !validateProductPriceTierDerivation(
      productPriceTier.derivationType,
      productPriceTier.derivationValue,
    )
  ) {
    throw new Error(
      `Invalid productPriceTier: derivationType "${productPriceTier.derivationType}" is incompatible with derivationValue ${productPriceTier.derivationValue}`,
    );
  }

  if (
    !validatePriceTierDerivation(
      priceTier.derivationType,
      priceTier.derivationValue,
    )
  ) {
    throw new Error(
      `Invalid priceTier: derivationType "${priceTier.derivationType}" is incompatible with derivationValue ${priceTier.derivationValue}`,
    );
  }

  if (productPriceTier.derivationType === "inherit") {
    const discount = getContainerDiscount(
      getContainerTypes(containerType),
      priceTierContainerDiscounts,
    );

    if (discount) {
      return calculateBaseDerivation(
        basePrice,
        discount.derivationType,
        discount.derivationValue,
      );
    }

    const { derivationType, derivationValue } = priceTier;

    if (derivationType === "custom") {
      return normalizePrice(basePrice);
    }

    if (derivationType === "flat" || derivationType === "percentage") {
      return calculateBaseDerivation(
        basePrice,
        derivationType,
        derivationValue,
      );
    }

    return normalizePrice(productPriceTier.price ?? basePrice);
  }

  const { derivationType, derivationValue } = productPriceTier;

  if (derivationType === "flat" || derivationType === "percentage") {
    return calculateBaseDerivation(basePrice, derivationType, derivationValue);
  }

  if (derivationType === "none") {
    return normalizePrice(basePrice);
  }

  return normalizePrice(productPriceTier.price ?? basePrice);
};

const normalizePrice = (price: number) => {
  return price < 0 ? 0 : price;
};

const calculateBaseDerivation = (
  basePrice: number,
  type: "flat" | "percentage",
  value: number | null,
): number => {
  if (type === "flat") {
    return normalizePrice(basePrice - (value ?? 0));
  }
  return normalizePrice(
    Math.round(basePrice - basePrice * ((value ?? 0) / 100)),
  );
};

const getContainerDiscount = (
  containerTypes: string[],
  discounts: Array<{
    containerType: "keg" | "can" | "cask" | "bottle" | "bagInBox";
    derivationType: "flat" | "percentage";
    derivationValue: number | null;
  }>,
) => {
  if (discounts.length === 0) return null;
  if (containerTypes.length === 0) return null;

  const knownContainers = ["keg", "can", "cask", "bottle", "bagInBox"] as const;
  const found = knownContainers.find((type) => containerTypes.includes(type));
  if (found) {
    return (
      discounts.find(
        (d) => d.containerType === found && d.derivationValue !== null,
      ) || null
    );
  }
  return null;
};
